import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError,
  categoriesApi,
  mediaApi,
  type Category,
  type CategoryInput,
} from '../api';
import { descendantsOf, flattenTree } from '../lib/tree';
import { ancestorWithDefaults, effectiveDefaultFields, hasInventoryFields } from '../lib/categories';
import { type FieldName } from '../lib/field-groups';
import { canWrite } from '../lib/permissions';
import { useAuth } from '../context/AuthContext';
import FieldGroupAccordion from '../components/FieldGroupAccordion';
import SingleImageUpload from '../components/SingleImageUpload';

export default function CategoriesPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth();
  const writeAccess = canWrite(user?.role);
  const [editing, setEditing] = useState<Category | null>(null);
  const [showForm, setShowForm] = useState(false);

  const { data: categories = [], isLoading } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.list,
  });

  const tree = flattenTree(categories);

  const removeMutation = useMutation({
    mutationFn: (id: number) => categoriesApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['categories'] }),
  });

  function startEdit(c: Category) {
    setEditing(c);
    setShowForm(true);
  }

  function startCreate() {
    setEditing(null);
    setShowForm(true);
  }

  function closeForm() {
    setShowForm(false);
    setEditing(null);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('categories.title')}</h1>
          <p className="text-sm text-slate-600">{t('categories.intro')}</p>
        </div>
        {writeAccess && !showForm && (
          <button
            onClick={startCreate}
            className="bg-slate-900 text-white rounded px-4 py-2 hover:bg-slate-800 text-sm font-medium"
          >
            {t('categories.new')}
          </button>
        )}
      </div>

      {showForm && (
        <CategoryForm
          categories={categories}
          editing={editing}
          onClose={closeForm}
        />
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-slate-500">{t('common.loading')}</div>
        ) : tree.length === 0 ? (
          <div className="p-8 text-center text-slate-500">{t('categories.empty')}</div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase">
                  {t('field.name')}
                </th>
                <th className="px-4 py-2 text-left text-xs font-semibold text-slate-700 uppercase">
                  {t('categories.inventory')}
                </th>
                {writeAccess && (
                  <th className="px-4 py-2 text-right text-xs font-semibold text-slate-700 uppercase">
                    {t('common.actions')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {tree.map((c) => (
                <tr key={c.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-4 py-2">
                    <div
                      className="flex items-center gap-2"
                      style={{ paddingLeft: `${c.depth * 24}px` }}
                    >
                      {c.color && (
                        <span
                          className="inline-block w-3 h-3 rounded"
                          style={{ backgroundColor: c.color }}
                        />
                      )}
                      <span className="font-medium text-slate-900">{c.name}</span>
                      {c.description && (
                        <span className="text-sm text-slate-500">— {c.description}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-sm text-slate-600">
                    {hasInventoryFields(c.id, categories) ? t('common.yes') : ''}
                  </td>
                  {writeAccess && (
                    <td className="px-4 py-2 text-right text-sm space-x-3">
                      <button
                        onClick={() => startEdit(c)}
                        className="text-slate-600 hover:text-slate-900 hover:underline"
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        onClick={() => {
                          if (confirm(t('categories.confirmDelete', { name: c.name }))) {
                            removeMutation.mutate(c.id);
                          }
                        }}
                        className="text-red-600 hover:text-red-800 hover:underline"
                      >
                        {t('common.delete')}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CategoryForm({
  categories,
  editing,
  onClose,
}: {
  categories: Category[];
  editing: Category | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [parentId, setParentId] = useState<number | null>(editing?.parentId ?? null);
  const [color, setColor] = useState(editing?.color ?? '');
  const [defaultFields, setDefaultFields] = useState<FieldName[]>(
    (editing?.defaultFields as FieldName[] | null) ?? [],
  );
  const [stagedImage, setStagedImage] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: async (data: CategoryInput) => {
      const result = editing
        ? await categoriesApi.update(editing.id, data)
        : await categoriesApi.create(data);
      // Hvis brugeren stagede et billede under oprettelse, upload det nu
      if (stagedImage && !editing) {
        try {
          await mediaApi.upload('categories', result.id, stagedImage, 'photo');
        } catch (err) {
          // Hovedoperationen lykkedes allerede — log billedfejl men vis ikke
          // den som dødsens fejl
          console.error('Failed to upload staged image:', err);
        }
      }
      return result;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['categories'] });
      qc.invalidateQueries({ queryKey: ['media'] });
      qc.invalidateQueries({ queryKey: ['storage-usage'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? t([`error.${err.message}`, 'error.UNKNOWN']) : t('error.UNKNOWN'));
    },
  });

  // Forhindrer cyklus ved redigering — sig selv og descendants kan ikke vælges som parent
  const blocked = editing ? descendantsOf(categories, editing.id) : new Set<number>();

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    saveMutation.mutate({
      name,
      description: description || null,
      parentId,
      defaultFields,
      color: color || null,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-4">
      <h2 className="text-lg font-semibold text-slate-900">
        {editing ? t('categories.editTitle') : t('categories.newTitle')}
      </h2>

      {error && <div className="rounded bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>}

      <label className="block">
        <span className="text-sm font-medium text-slate-700">{t('field.name')}</span>
        <input
          type="text"
          required
          maxLength={200}
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
        />
      </label>

      <label className="block">
        <span className="text-sm font-medium text-slate-700">{t('field.description')}</span>
        <textarea
          rows={2}
          maxLength={2000}
          value={description ?? ''}
          onChange={(e) => setDescription(e.target.value)}
          className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
        />
      </label>

      <div className="grid grid-cols-2 gap-4">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">{t('field.parent')}</span>
          <select
            value={parentId ?? ''}
            onChange={(e) => setParentId(e.target.value ? Number(e.target.value) : null)}
            className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
          >
            <option value="">{t('field.none')}</option>
            {flattenTree(categories)
              .filter((c) => !blocked.has(c.id))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {'— '.repeat(c.depth)}
                  {c.name}
                </option>
              ))}
          </select>
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700">{t('field.color')}</span>
          <input
            type="color"
            value={color || '#94a3b8'}
            onChange={(e) => setColor(e.target.value)}
            className="mt-1 block w-full h-10 rounded border border-slate-300"
          />
        </label>
      </div>

      <div className="border-t border-slate-200 pt-4">
        <div className="text-sm font-medium text-slate-700 mb-2">{t('field.image')}</div>
        {editing ? (
          <SingleImageUpload target="categories" targetId={editing.id} />
        ) : (
          <SingleImageUpload
            target="categories"
            stagedBlob={stagedImage}
            onStaged={setStagedImage}
          />
        )}
      </div>

      <div className="border-t border-slate-200 pt-4">
        <div className="text-sm font-medium text-slate-700">
          {t('categories.defaultFields')}
        </div>
        <p className="text-xs text-slate-500 mt-0.5 mb-3">
          {t('categories.defaultFieldsHint')}
        </p>
        {(() => {
          // Beregn arvede felter fra parent-chain (ekskluder denne kategoris egne felter
          // hvis den er ved at blive redigeret, så vi ikke får dem dobbelt).
          const inherited = effectiveDefaultFields(parentId, categories);
          const ancestor = ancestorWithDefaults(parentId, categories);
          const showInheritedHint = ancestor && (!editing || ancestor.id !== editing.id);
          return (
            <>
              <FieldGroupAccordion
                selected={defaultFields}
                onChange={setDefaultFields}
                inherited={inherited as FieldName[]}
              />
              {showInheritedHint && (
                <p className="text-xs text-slate-500 mt-2">
                  {t('categories.inheritedFromAncestor', { name: ancestor!.name })}
                </p>
              )}
            </>
          );
        })()}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded text-sm"
        >
          {t('common.cancel')}
        </button>
        <button
          type="submit"
          disabled={saveMutation.isPending}
          className="bg-slate-900 text-white rounded px-4 py-2 hover:bg-slate-800 text-sm font-medium disabled:opacity-50"
        >
          {saveMutation.isPending ? t('common.working') : t('common.save')}
        </button>
      </div>
    </form>
  );
}
